import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { GoldenOzPage } from './GoldenOzPage';
import { ShareArtwork } from './ShareArtwork';
import { ArrowLeft, ArrowRight } from 'lucide-react';

/**
 * Dedicated standalone route for the Golden Oz memorial artwork.
 * URL: /golden-oz
 * Improves SEO indexing — Google treats it as a discrete, shareable page.
 */
export const GoldenOzRoute = () => {
  const { isRTL } = useLanguage();
  const BackIcon = isRTL ? ArrowRight : ArrowLeft;

  // Update document title + canonical for SEO on this dedicated route.
  useEffect(() => {
    const prevTitle = document.title;
    document.title = isRTL
      ? 'גולדן עוז (Golden Oz) | מיצב הנצחה 7.10 בנחל עוז - M\'art מיקרומינרל'
      : 'Golden Oz | 7.10 Memorial Installation at Nahal Oz - M\'art MicroMineral';

    // Update canonical URL to the dedicated page
    let canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute('href') : null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://www.mart-mineral.com/golden-oz');

    // Update OG URL
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const prevOgUrl = ogUrl ? ogUrl.getAttribute('content') : null;
    if (ogUrl) ogUrl.setAttribute('content', 'https://www.mart-mineral.com/golden-oz');

    // Scroll to top on mount
    window.scrollTo({ top: 0, behavior: 'instant' });

    return () => {
      document.title = prevTitle;
      if (prevCanonical && canonical) canonical.setAttribute('href', prevCanonical);
      if (prevOgUrl && ogUrl) ogUrl.setAttribute('content', prevOgUrl);
    };
  }, [isRTL]);

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? 'rtl' : 'ltr'}>
      <Header />

      <main className="pt-4">
        {/* Back navigation */}
        <div className="max-w-5xl mx-auto px-4 pt-4 pb-2">
          <Link
            to="/"
            data-testid="golden-oz-back-link"
            className="inline-flex items-center gap-2 text-sm text-[#8C661F] hover:text-[#3A2E18] transition-colors group"
          >
            <BackIcon className="w-4 h-4 transition-transform group-hover:-translate-x-0.5 rtl:group-hover:translate-x-0.5" />
            <span className="font-medium">
              {isRTL ? 'חזרה למחשבון' : 'Back to calculator'}
            </span>
          </Link>
        </div>

        <GoldenOzPage />
        <ShareArtwork />
      </main>

      <Footer />
    </div>
  );
};

export default GoldenOzRoute;
